import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ResponseArtifactDTO,
  MultipleChoiceArtifact,
  ArtifactChoice,
  YesOrNoArtifact,
  FormInputArtifact,
  FormField,
  DesiredActionResponse,
  GoogleLinkedAccount,
  GetGoogleLinkedAccountsResponse,
} from '@dreamai/types';
import api from '../../../api';
import { ReactComponent as TickSelectIcon } from '../../../icons/tick-select.svg';
import { ReactComponent as SpeechBubbleIcon } from '../../../icons/speech-bubble.svg';
import { ReactComponent as NoLongerNeededIcon } from '../../../icons/no-longer-needed.svg';
import {
  useReplyToActions,
  ReplyToItem,
} from '../../../contexts/ReplyToContext';
import { useAgent } from '../../../contexts/AgentContext';
import SpendingApprovalModal, { CardSelection } from './SpendingApprovalModal';
import { isOrganisationCard } from './organisationSpend';
import { artifactCheckoutResume } from '../HostedCheckoutArtifactResume';
import ImageCard from '../../shared/ImageCard';
import FileLink from '../../shared/FileLink';
import ResolvedImage from '../../shared/ResolvedImage';
import ConfirmationSpecialView, {
  hasSpecialView,
} from '../../ConfirmationSpecialView';
import MultipleChoiceFullScreen from './MultipleChoiceFullScreen';
import { HotelBookingSelection } from './hotelArtifact';
import { FlightBookingSelection } from './flightArtifact';
import { areDreamRailChoices } from './railArtifact';
import YesOrNoFullScreen from './YesOrNoFullScreen';
import FormInputFullScreen from './FormInputFullScreen';
import SecureCredentialModal, {
  SecureCredentialSubmitOptions,
} from './SecureCredentialModal';
import SecureInputRequestModal from './SecureInputRequestModal';
import type { SheetPresentation } from './TransactionalSheet';
import PaymentChallengeModal from './PaymentChallengeModal';
import PurchaseApprovalModal from './PurchaseApprovalModal';
import ExpenseReviewModal from './ExpenseReviewModal';
import { isExpenseReviewArtifact } from './expenseReviewArtifact';
import {
  getMandateSecurePayment,
  getPurchaseApproval,
  isLiveChallengeArtifact,
} from './purchaseApprovalArtifact';
import {
  SecureCredentialSubmitResult,
  submitErrorFromUnknown,
} from './credentialSubmitResult';
import AddPaymentCardModal, {
  AddPaymentCardResult,
} from './AddPaymentCardModal';
import AddTodoModal from './AddTodoModal';
import DreamieAvatarButton from '../DreamieAvatarButton';
import { hasSecureRequestId } from './secureInputArtifact';
import './response-artifact-modal.scss';
import './spending-approval-modal.scss';

type TravelBookingSelection = HotelBookingSelection | FlightBookingSelection;

interface ResponseArtifactModalProps {
  show: boolean;
  onClose: () => void;
  artifacts: ResponseArtifactDTO[];
  initialIndex?: number;
  onSubmitResponse?: (
    artifact: ResponseArtifactDTO,
    response: any,
    mandateOptions?: {
      createMandate: boolean;
      mandateItems: Array<{
        actionLine: string;
        descriptionLine: string;
        price?: number;
        price_currency?: string;
        isDeposit?: boolean;
      }>;
    },
    cardSelection?: CardSelection,
  ) => Promise<void>;
  colorVariants?: {
    base: string;
    midDarker: string;
    darker: string;
    lighter: string;
  };
  /** If true, shows previous selection but disables the primary action button */
  readOnly?: boolean;
  /** If true, shows a loading skeleton instead of content */
  loading?: boolean;
  /** If set, shows an error state with this message */
  error?: string | null;
  /** Called immediately when the user takes an action (submit or dismiss) for optimistic UI updates */
  onOptimisticAction?: (
    artifact: ResponseArtifactDTO,
    action: 'submit' | 'dismiss',
  ) => void;
  /** When true, 'Chat about' renders as an <a> tag that navigates to the artifact's parent thread with reply params. Enables Cmd+Click to open in new tab. */
  navigateOnChat?: boolean;
  /** When provided, 'Chat about this' calls this callback instead of navigating or adding reply-to. Used to open a mini thread panel. */
  onChatAbout?: (artifact: ResponseArtifactDTO) => void;
  /** Share token for anonymous payment actions on a public artifact page. */
  shareToken?: string;
  /** Agent WhatsApp number for the artifact header (used on public share pages). */
  agentWhatsappNumber?: string;
  /** Assistant name when no agent context is available (public share pages). */
  agentName?: string;
}

const ResponseArtifactModal: React.FC<ResponseArtifactModalProps> = ({
  show,
  onClose,
  artifacts,
  initialIndex = 0,
  onSubmitResponse,
  colorVariants,
  readOnly = false,
  loading = false,
  onOptimisticAction,
  error = null,
  navigateOnChat = false,
  onChatAbout,
  shareToken,
  agentWhatsappNumber,
  agentName: agentNameProp,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [formValues, setFormValues] = useState<
    Record<string, string | number | null>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [showSpendingApproval, setShowSpendingApproval] = useState(false);
  const [activeImageMap, setActiveImageMap] = useState<Record<string, string>>(
    {},
  ); // Still used for preloading; ImageCard manages its own active image state
  const { addReplyTo } = useReplyToActions();
  const { agents } = useAgent();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  // Connected email accounts for from_email_address selects in desired actions
  const [connectedEmails, setConnectedEmails] = useState<string[]>([]);
  useEffect(() => {
    if (shareToken) return;
    let cancelled = false;
    const fetchEmails = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetGoogleLinkedAccountsResponse>(
          '/google-link',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) {
          setConnectedEmails(
            res.data.linkedAccounts.map((a: GoogleLinkedAccount) => a.email),
          );
        }
      } catch {
        // Not critical — selects will just be empty
      }
    };
    fetchEmails();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, shareToken]);

  // Preload images from all artifacts when they change
  useEffect(() => {
    const imageUrls: string[] = [];
    artifacts.forEach((artifact) => {
      if (
        artifact.artifactType === 'multiple_choice' &&
        'choices' in artifact
      ) {
        (artifact as MultipleChoiceArtifact).choices.forEach((choice) => {
          if (choice.imageUrl) {
            imageUrls.push(choice.imageUrl);
          }
          if (choice.additionalImages) {
            imageUrls.push(...choice.additionalImages);
          }
        });
      }
    });

    // Preload images
    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, [artifacts]);

  // Reset state when modal opens or closes
  // Pre-populate selections from userResponse if artifact was already actioned
  useEffect(() => {
    setShowSpendingApproval(false);
    if (show) {
      setCurrentIndex(initialIndex);
      const artifact = artifacts[initialIndex];
      if (artifact?.userResponse) {
        // Pre-populate from previous response
        if (
          artifact.artifactType === 'multiple_choice' &&
          artifact.userResponse.selectedChoiceIds
        ) {
          setSelectedChoices(artifact.userResponse.selectedChoiceIds);
          setFormValues({});
        } else if (
          artifact.artifactType === 'yes_or_no' &&
          artifact.userResponse.selection
        ) {
          setSelectedChoices([artifact.userResponse.selection]);
          setFormValues({});
        } else if (
          artifact.artifactType === 'form_input' &&
          artifact.userResponse?.values
        ) {
          setFormValues(artifact.userResponse.values);
          setSelectedChoices([]);
        } else {
          setSelectedChoices([]);
          setFormValues({});
        }
      } else {
        setSelectedChoices([]);
        setFormValues({});
      }
    } else {
      // Reset all state when modal closes
      setSelectedChoices([]);
      setFormValues({});
    }
  }, [show, initialIndex]);

  const currentArtifact = artifacts[currentIndex];

  const handleChoiceSelect = (choiceId: string) => {
    // Don't allow selection changes in read-only mode
    if (readOnly) return;

    // Yes/No artifacts act as radio buttons (single selection)
    if (currentArtifact?.artifactType === 'yes_or_no') {
      setSelectedChoices((prev) => (prev.includes(choiceId) ? [] : [choiceId]));
      return;
    }

    setSelectedChoices((prev) =>
      prev.includes(choiceId)
        ? prev.filter((id) => id !== choiceId)
        : [...prev, choiceId],
    );
  };

  // Build spend items for the current artifact + user selection.
  // Works across multiple_choice (per-choice prices) and form_input (submitSpend).
  const pendingSpendItems = useMemo((): Array<{
    id: string;
    actionLine: string;
    descriptionLine: string;
    price: number;
    price_currency: string;
    definitivePrice?: number;
    approxPriceRange?: [number, number];
    priceEstimate?: number;
    deliveryAllowance?: number;
    varianceAllowance?: number;
    varianceNeedReason?: string;
    purchaseSummary?: string;
    purchaseLink?: string;
    delivery?: string;
    deliveryAddress?: string;
    isDeposit?: boolean;
  }> => {
    if (!currentArtifact) return [];

    const isDeposit =
      currentArtifact.artifactType === 'multiple_choice'
        ? ((currentArtifact as MultipleChoiceArtifact).desiredActions?.some(
            (a) => a.key === 'reserve',
          ) ?? false)
        : false;

    if (currentArtifact.artifactType === 'multiple_choice') {
      const artifact = currentArtifact as MultipleChoiceArtifact;
      const selected = artifact.choices.filter((c) =>
        selectedChoices.includes(c.choiceId),
      );
      return selected
        .filter((c) => {
          // JSONB data may arrive with snake_case keys from the DB
          const a = c as any;
          return (
            (c.definitivePrice ?? a.definitive_price) != null ||
            (c.approxPriceRange ?? a.approx_price_range) != null ||
            (c.priceEstimate ?? a.price_estimate) != null
          );
        })
        .map((c) => {
          const a = c as any; // snake_case fallback for JSONB data
          const defPrice = c.definitivePrice ?? a.definitive_price;
          const range = c.approxPriceRange ?? a.approx_price_range;
          const estimate = c.priceEstimate ?? a.price_estimate;
          const includesDelivery =
            c.priceIncludesDelivery ?? a.price_includes_delivery ?? false;
          const deliveryAllow = !includesDelivery
            ? (c.deliveryAllowance ?? a.delivery_allowance ?? 0)
            : 0;
          const varianceAllow =
            c.varianceAllowance ?? a.variance_allowance ?? 0;
          const varianceReason = c.varianceNeedReason ?? a.variance_need_reason;
          const currency = c.priceCurrency ?? a.price_currency ?? 'GBP';

          const basePrice =
            defPrice ?? (range ? range[1] : undefined) ?? estimate ?? 0;
          const maxPrice = basePrice + deliveryAllow + varianceAllow;

          return {
            id: c.choiceId,
            actionLine: artifact.actionVerb || 'Book',
            descriptionLine: c.choiceTitle,
            price: maxPrice,
            price_currency: currency,
            definitivePrice: defPrice,
            approxPriceRange: range,
            priceEstimate: estimate,
            deliveryAllowance: deliveryAllow || undefined,
            varianceAllowance: varianceAllow || undefined,
            varianceNeedReason: varianceReason,
            isDeposit,
          };
        });
    }

    if (currentArtifact.artifactType === 'form_input') {
      const artifact = currentArtifact as FormInputArtifact;
      if (artifact.submitSpend && artifact.submitSpend.price > 0) {
        return [
          {
            id: 'form_submit',
            actionLine: artifact.submitLabel || 'Submit',
            descriptionLine:
              artifact.submitSpend.purchaseSummary || artifact.title,
            price: artifact.submitSpend.price,
            price_currency: artifact.submitSpend.price_currency,
            purchaseSummary: artifact.submitSpend.purchaseSummary,
            purchaseLink: artifact.submitSpend.purchaseLink,
            delivery: artifact.submitSpend.delivery,
            deliveryAddress: artifact.submitSpend.deliveryAddress,
          },
        ];
      }
    }

    return [];
  }, [currentArtifact, selectedChoices]);

  // Whether the pending spend items are for a refundable deposit (reserve) vs a purchase (buy)
  const isDepositApproval = useMemo(() => {
    if (!currentArtifact) return false;
    if (currentArtifact.artifactType === 'multiple_choice') {
      const artifact = currentArtifact as MultipleChoiceArtifact;
      return artifact.desiredActions?.some((a) => a.key === 'reserve') ?? false;
    }
    return false;
  }, [currentArtifact]);

  const handleSubmit = async () => {
    if (!onSubmitResponse || !currentArtifact) return;

    // Dream fares need preparation, not a generic spending mandate.
    if (
      currentArtifact.artifactType === 'multiple_choice' &&
      areDreamRailChoices(currentArtifact.choices ?? [], selectedChoices)
    ) {
      await executeSubmit();
      return;
    }

    // If the action has spend implications, show spending approval first
    if (pendingSpendItems.length > 0) {
      setShowSpendingApproval(true);
      return;
    }

    await executeSubmit();
  };

  const executeSubmit = async (
    withMandate = false,
    desiredAction?: DesiredActionResponse,
    explicitMandateItems?: Array<{
      actionLine: string;
      descriptionLine: string;
      price: number;
      price_currency: string;
      isDeposit?: boolean;
    }>,
    travelSelection?: TravelBookingSelection,
  ) => {
    if (!onSubmitResponse || !currentArtifact) return;

    const cardSelection = withMandate ? cardSelectionRef.current : undefined;
    setSubmitting(true);
    // Optimistically update before the API call. Organisation-funded spend is
    // charged as part of the submit and may be declined, and a hotel Book
    // against a live quote is refused once that quote lapses, so wait for those.
    const deferOptimistic =
      isOrganisationCard(cardSelection) ||
      (travelSelection != null &&
        'quoteId' in travelSelection &&
        !!travelSelection.quoteId);
    if (!deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
    try {
      let mandateOptions;
      if (withMandate) {
        // Use explicit mandate items from MC if provided, otherwise fall back to pendingSpendItems
        const items =
          explicitMandateItems ??
          (pendingSpendItems.length > 0
            ? pendingSpendItems.map((item) => ({
                actionLine: item.actionLine,
                descriptionLine: item.descriptionLine,
                price: item.price,
                price_currency: item.price_currency,
                isDeposit: item.isDeposit,
              }))
            : []);
        if (items.length > 0) {
          mandateOptions = { createMandate: true, mandateItems: items };
        }
      }
      await onSubmitResponse(
        currentArtifact,
        {
          selectedChoiceIds: travelSelection
            ? [travelSelection.choiceId]
            : selectedChoices,
          ...(travelSelection?.offerId
            ? { selectedOfferId: travelSelection.offerId }
            : {}),
          ...(travelSelection &&
          'guest' in travelSelection &&
          travelSelection.guest
            ? { guest: travelSelection.guest }
            : {}),
          ...(travelSelection &&
          'quoteId' in travelSelection &&
          travelSelection.quoteId
            ? { hotelQuoteId: travelSelection.quoteId }
            : {}),
          ...(travelSelection &&
          'passengers' in travelSelection &&
          travelSelection.passengers
            ? { passengers: travelSelection.passengers }
            : {}),
          ...(desiredAction ? { desiredAction } : {}),
        },
        mandateOptions,
        cardSelection,
      );
      if (deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
    } finally {
      setSubmitting(false);
    }
  };

  const cardSelectionRef = React.useRef<CardSelection | undefined>(undefined);

  // The spending sheet stays open while an organisation charge is in flight
  // so a declined charge can be shown there; other cards close it right away.
  const handleApproveSpending = async (cardSel?: CardSelection) => {
    cardSelectionRef.current = cardSel;
    const keepSheetOpen = isOrganisationCard(cardSel);
    if (!keepSheetOpen) setShowSpendingApproval(false);
    if (currentArtifact?.artifactType === 'form_input') {
      await executeFormSubmit(true);
    } else {
      await executeSubmit(true);
    }
    if (keepSheetOpen) setShowSpendingApproval(false);
  };

  const handleCancelSpending = () => {
    setShowSpendingApproval(false);
  };

  const handleAcknowledge = async () => {
    if (!onSubmitResponse || !currentArtifact) return;

    setSubmitting(true);
    // Optimistically update before the API call
    onOptimisticAction?.(currentArtifact, 'dismiss');
    try {
      await onSubmitResponse(currentArtifact, {
        acknowledged: true,
        dismissed: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Allow rendering if loading or error, even without artifacts
  if (!currentArtifact && !loading && !error) {
    return null;
  }

  const renderNavigationHeader = () => (
    <div className='navigation-header'>
      <h1>{readOnly ? 'You made a choice' : 'Needs your attention'}</h1>
      <button className='modal-close-btn' onClick={onClose} aria-label='Close'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            d='M12 4L4 12M4 4L12 12'
            stroke='#374562'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>
    </div>
  );

  const renderLoadingSkeleton = () => (
    <div className='loading-skeleton'>
      <div className='skeleton-header' />
      <div className='skeleton-title' />
      <div className='skeleton-content'>
        <div className='skeleton-line' />
        <div className='skeleton-line short' />
        <div className='skeleton-line' />
      </div>
      <div className='skeleton-choices'>
        <div className='skeleton-choice' />
        <div className='skeleton-choice' />
        <div className='skeleton-choice' />
      </div>
    </div>
  );

  const renderError = () => (
    <div className='error-content'>
      <p className='error-message'>{error}</p>
      <button className='error-close-btn' onClick={onClose}>
        Close
      </button>
    </div>
  );

  const renderChoiceText = (
    choice: MultipleChoiceArtifact['choices'][number],
  ) => (
    <div className='choice-text'>
      <b>{choice.choiceTitle}</b>
      {choice.choiceBlurb && (
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
          {choice.choiceBlurb}
        </ReactMarkdown>
      )}
      {choice.fields && choice.fields.length > 0 && (
        <div className='choice-fields'>
          {choice.fields.map((field, idx) => (
            <div key={idx} className='choice-field'>
              <span className='field-label'>{field.label}</span>
              <span className='field-value'>{field.value}</span>
            </div>
          ))}
        </div>
      )}
      {choice.links && choice.links.length > 0 && (
        <div className='choice-links'>
          {choice.links.map((link, idx) => (
            <FileLink
              key={idx}
              url={link.url}
              onClick={(e) => e.stopPropagation()}
              style={
                colorVariants ? { color: colorVariants.darker } : undefined
              }
            >
              {link.label}
            </FileLink>
          ))}
        </div>
      )}
    </div>
  );

  const renderMultipleChoice = (artifact: MultipleChoiceArtifact) => (
    <div className='choices-container'>
      {artifact.choices.map((choice) => (
        <div
          key={choice.choiceId}
          className={`choice-item ${selectedChoices.includes(choice.choiceId) ? 'selected' : ''} ${readOnly ? 'read-only' : ''} ${choice.imageUrl ? 'has-image' : ''} ${choice.imageHeavy ? 'image-heavy' : ''}`}
          onClick={() =>
            !choice.imageHeavy && handleChoiceSelect(choice.choiceId)
          }
          role='button'
          tabIndex={0}
        >
          {choice.imageHeavy ? (
            <ImageCard
              id={choice.choiceId}
              title={choice.choiceTitle}
              blurb={choice.choiceBlurb}
              imageUrl={choice.imageUrl}
              additionalImages={choice.additionalImages}
              fields={choice.fields}
              links={choice.links}
              selected={selectedChoices.includes(choice.choiceId)}
              onSelect={() => handleChoiceSelect(choice.choiceId)}
              readOnly={readOnly}
              accentColor={colorVariants?.darker}
            />
          ) : (
            <>
              <div
                className={`choice-tick ${selectedChoices.includes(choice.choiceId) ? 'selected' : ''}`}
              >
                <TickSelectIcon />
              </div>
              {choice.imageUrl && (
                <div className='choice-image'>
                  <ResolvedImage
                    src={choice.imageUrl}
                    alt={choice.choiceTitle}
                  />
                </div>
              )}
              {renderChoiceText(choice)}
            </>
          )}
        </div>
      ))}
    </div>
  );

  const renderYesOrNo = (artifact: YesOrNoArtifact) => (
    <div className='choices-container yes-or-no'>
      <div
        className={`choice-item ${selectedChoices.includes('yes') ? 'selected' : ''}`}
        onClick={() => handleChoiceSelect('yes')}
        role='button'
        tabIndex={0}
        style={
          selectedChoices.includes('yes') && colorVariants
            ? {
                backgroundColor: colorVariants.lighter,
                borderColor: colorVariants.darker,
              }
            : undefined
        }
      >
        <div
          className={`choice-tick ${selectedChoices.includes('yes') ? 'selected' : ''}`}
          style={
            selectedChoices.includes('yes') && colorVariants
              ? {
                  backgroundColor: colorVariants.darker,
                  borderColor: colorVariants.darker,
                }
              : undefined
          }
        >
          <TickSelectIcon />
        </div>
        <div className='choice-text'>
          <b>Yes</b>
          {artifact.yesBlurb && (
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {artifact.yesBlurb}
            </ReactMarkdown>
          )}
        </div>
      </div>
      <div
        className={`choice-item ${selectedChoices.includes('no') ? 'selected' : ''}`}
        onClick={() => handleChoiceSelect('no')}
        role='button'
        tabIndex={0}
        style={
          selectedChoices.includes('no') && colorVariants
            ? {
                backgroundColor: colorVariants.lighter,
                borderColor: colorVariants.darker,
              }
            : undefined
        }
      >
        <div
          className={`choice-tick ${selectedChoices.includes('no') ? 'selected' : ''}`}
          style={
            selectedChoices.includes('no') && colorVariants
              ? {
                  backgroundColor: colorVariants.darker,
                  borderColor: colorVariants.darker,
                }
              : undefined
          }
        >
          <TickSelectIcon />
        </div>
        <div className='choice-text'>
          <b>No</b>
          {artifact.noBlurb && (
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {artifact.noBlurb}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );

  const handleFormFieldChange = (
    fieldId: string,
    value: string | number | null,
  ) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const renderFormField = (field: FormField) => {
    const value = formValues[field.fieldId] ?? '';

    switch (field.fieldType) {
      case 'text':
        return (
          <input
            type='text'
            className='form-input'
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) =>
              handleFormFieldChange(field.fieldId, e.target.value)
            }
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'number':
        return (
          <input
            type='number'
            className='form-input'
            placeholder={field.placeholder}
            value={value as number}
            onChange={(e) =>
              handleFormFieldChange(
                field.fieldId,
                e.target.value ? Number(e.target.value) : null,
              )
            }
            min={field.min}
            max={field.max}
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'date':
        return (
          <input
            type='date'
            className='form-input'
            value={value as string}
            onChange={(e) =>
              handleFormFieldChange(field.fieldId, e.target.value)
            }
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'textarea':
        return (
          <textarea
            className='form-textarea'
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) =>
              handleFormFieldChange(field.fieldId, e.target.value)
            }
            rows={field.rows || 4}
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'select':
        return (
          <select
            className='form-select'
            value={value as string}
            onChange={(e) =>
              handleFormFieldChange(field.fieldId, e.target.value)
            }
            required={field.required}
            disabled={readOnly}
          >
            <option value=''>{field.placeholder || 'Select an option'}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'key_value':
        return <div className='key-value-display'>{field.value || ''}</div>;
      default:
        return null;
    }
  };

  const renderFormInput = (artifact: FormInputArtifact) => (
    <div className='form-input-container'>
      {artifact.fields.map((field) => (
        <div key={field.fieldId} className='form-field'>
          <label className='form-label'>
            {field.label}
            {field.required && <span className='required-indicator'>*</span>}
          </label>
          {renderFormField(field)}
        </div>
      ))}
    </div>
  );

  const handleFormSubmit = async () => {
    if (!onSubmitResponse || !currentArtifact) return;

    // If form has spend implications, show spending approval first
    if (pendingSpendItems.length > 0) {
      setShowSpendingApproval(true);
      return;
    }

    await executeFormSubmit();
  };

  const executeFormSubmit = async (withMandate = false) => {
    if (!onSubmitResponse || !currentArtifact) return;

    const cardSelection = withMandate ? cardSelectionRef.current : undefined;
    setSubmitting(true);
    const deferOptimistic = isOrganisationCard(cardSelection);
    if (!deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
    try {
      const mandateOptions =
        withMandate && pendingSpendItems.length > 0
          ? {
              createMandate: true,
              mandateItems: pendingSpendItems.map((item) => ({
                actionLine: item.actionLine,
                descriptionLine: item.descriptionLine,
                price: item.price,
                price_currency: item.price_currency,
                isDeposit: item.isDeposit,
              })),
            }
          : undefined;
      await onSubmitResponse(
        currentArtifact,
        { values: formValues },
        mandateOptions,
        cardSelection,
      );
      if (deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (currentArtifact?.artifactType !== 'form_input') return false;
    const artifact = currentArtifact as FormInputArtifact;
    return artifact.fields.every((field) => {
      if (!field.required) return true;
      const value = formValues[field.fieldId];
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Check if this artifact is linked to a confirmation query
  const isConfirmationLinked = !!currentArtifact?.confirmationId;
  const confirmationMethodName = currentArtifact?.confirmationMethodName;
  const confirmationMethodInput = currentArtifact?.confirmationMethodInput;
  const hasConfirmationSpecialView =
    !!confirmationMethodName && hasSpecialView(confirmationMethodName);

  const renderConfirmationInfoContent = (artifact: YesOrNoArtifact) => (
    <div className='confirmation-info-content'>
      {artifact.noBlurb && (
        <div className='confirmation-info-blurb reject-blurb'>
          <span className='blurb-label reject-label'>If rejected</span>
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
            {artifact.noBlurb}
          </ReactMarkdown>
        </div>
      )}
      {artifact.yesBlurb && (
        <div className='confirmation-info-blurb approve-blurb'>
          <span className='blurb-label approve-label'>If approved</span>
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
            {artifact.yesBlurb}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    // If the artifact wraps a confirmation with a rich special view, render that
    // instead of the basic form/yes-no fields
    if (hasConfirmationSpecialView) {
      return (
        <ConfirmationSpecialView
          methodName={confirmationMethodName!}
          methodInput={confirmationMethodInput}
        />
      );
    }

    // For confirmation-linked yes_or_no artifacts, render as informational text
    // (user approves/rejects via footer buttons, not by selecting yes/no)
    if (isConfirmationLinked && currentArtifact.artifactType === 'yes_or_no') {
      return renderConfirmationInfoContent(currentArtifact as YesOrNoArtifact);
    }

    switch (currentArtifact.artifactType) {
      case 'multiple_choice':
        return renderMultipleChoice(currentArtifact as MultipleChoiceArtifact);
      case 'yes_or_no':
        return renderYesOrNo(currentArtifact as YesOrNoArtifact);
      case 'form_input':
        return renderFormInput(currentArtifact as FormInputArtifact);
      case 'confirmation_query':
        // Confirmation queries are handled by the existing confirmation system
        return null;
      default:
        return null;
    }
  };

  const getTitle = () => {
    if ('title' in currentArtifact && currentArtifact.title) {
      return currentArtifact.title;
    }
    return 'Action required';
  };

  const getOverview = () => {
    if ('overview' in currentArtifact && currentArtifact.overview) {
      return currentArtifact.overview;
    }
    return null;
  };

  const getChatButtonText = () => {
    if (currentArtifact.artifactType === 'multiple_choice') {
      const terminology = (currentArtifact as MultipleChoiceArtifact)
        .terminology || { singular: 'option', plural: 'options' };
      const term =
        selectedChoices.length === 1
          ? terminology.singular
          : terminology.plural;
      return `Chat about ${selectedChoices.length === 1 ? 'this' : 'these'} ${term}`;
    }
    return 'Chat about this';
  };

  const getActionButtonText = () => {
    if (currentArtifact.artifactType === 'multiple_choice') {
      const verb =
        (currentArtifact as MultipleChoiceArtifact).actionVerb || 'Confirm';
      const terminology = (currentArtifact as MultipleChoiceArtifact)
        .terminology || { singular: 'option', plural: 'options' };
      const term =
        selectedChoices.length === 1
          ? terminology.singular
          : terminology.plural;
      return `${verb} ${selectedChoices.length === 1 ? 'this' : 'these'} ${term}`;
    }
    if (currentArtifact.artifactType === 'yes_or_no') {
      if (selectedChoices.includes('yes')) return 'Confirm';
      if (selectedChoices.includes('no')) return 'Confirm';
      return null;
    }
    if (currentArtifact.artifactType === 'form_input') {
      return (currentArtifact as FormInputArtifact).submitLabel || 'Submit';
    }
    return null;
  };

  // Public /artifact/:shareToken pages are the page itself — nothing behind
  // them to close back to — so transactional views render standalone.
  const sheetPresentation: SheetPresentation = shareToken
    ? 'standalone'
    : 'modal';

  // Coded expenses waiting to be filed: the review page is read-only and only
  // its Submit button PATCHes the artifact.
  if (show && isExpenseReviewArtifact(currentArtifact) && !loading && !error) {
    return (
      <ExpenseReviewModal
        show={show}
        artifact={currentArtifact}
        readOnly={readOnly || submitting}
        presentation={sheetPresentation}
        onClose={onClose}
        onSubmit={async (response) => {
          if (!onSubmitResponse) return;
          setSubmitting(true);
          try {
            await onSubmitResponse(currentArtifact, response);
            onOptimisticAction?.(currentArtifact, 'submit');
          } finally {
            setSubmitting(false);
          }
        }}
      />
    );
  }

  // Render SpendingApprovalModal directly for spending_approval artifacts
  if (
    show &&
    currentArtifact?.artifactType === 'spending_approval' &&
    !loading &&
    !error
  ) {
    const spendAgent =
      agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ||
      agents[0];

    // Secure-payment approval: an exact merchant/basket/total/card mandate
    // bound server-side. Signed in it uses the authenticated routes; from the
    // card's own share link it uses their share-token twins (no login).
    const purchase = getPurchaseApproval(currentArtifact);
    if (purchase) {
      return (
        <PurchaseApprovalModal
          show={show}
          purchase={purchase}
          threadId={currentArtifact.parentThreadId}
          assistantName={spendAgent?.nickname ?? spendAgent?.name ?? undefined}
          readOnly={readOnly || submitting}
          shareToken={shareToken}
          onApproved={() => {
            onOptimisticAction?.(currentArtifact, 'submit');
            onClose();
          }}
          onRejected={() => {
            onOptimisticAction?.(currentArtifact, 'dismiss');
            onClose();
          }}
          onClose={onClose}
        />
      );
    }

    const spendItems: any[] = (currentArtifact as any).spendItems || [];
    const isSpendDeposit = (currentArtifact as any).isDeposit || false;

    const replyToItems: ReplyToItem[] = spendItems.map(
      (item: any, i: number) => ({
        id: `spend-${currentArtifact.id}-${i}`,
        actionLine: 'Purchase',
        descriptionLine: item.description || '',
        bookable: true,
        price: item.price,
        price_currency: item.currency,
        purchaseLink: item.purchaseLink,
        delivery: item.delivery,
        deliveryAddress: item.deliveryAddress,
        merchantName: item.merchantName || undefined,
      }),
    );

    const spendMerchantName: string | undefined =
      spendItems.find((item: any) => item.merchantName)?.merchantName ||
      undefined;

    const handleSpendApprove = async (cardSel?: CardSelection) => {
      if (!onSubmitResponse || !currentArtifact) return;
      setSubmitting(true);
      const deferOptimistic = isOrganisationCard(cardSel);
      if (!deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
      try {
        await onSubmitResponse(
          currentArtifact,
          {
            approved: true,
            ...(cardSel?.approvedSpendPermission != null && {
              approvedSpendPermission: cardSel.approvedSpendPermission,
            }),
            ...(cardSel?.people?.length && { people: cardSel.people }),
          },
          undefined,
          cardSel,
        );
        if (deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
      } finally {
        setSubmitting(false);
      }
    };

    const handleSpendReject = async () => {
      if (!onSubmitResponse || !currentArtifact) return;
      setSubmitting(true);
      onOptimisticAction?.(currentArtifact, 'dismiss');
      try {
        await onSubmitResponse(currentArtifact, { approved: false });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <SpendingApprovalModal
        key={currentArtifact.id}
        show={show}
        onApprove={handleSpendApprove}
        onCancel={onClose}
        onReject={handleSpendReject}
        items={replyToItems}
        isDeposit={isSpendDeposit}
        acceptedPaymentMethods={currentArtifact.acceptedPaymentMethods}
        agentImageUrl={spendAgent?.image_url ?? undefined}
        assistantName={spendAgent?.nickname ?? spendAgent?.name ?? undefined}
        context={currentArtifact.overview}
        structuredDetails={currentArtifact.details}
        exactAmount={currentArtifact.exactAmount}
        merchantName={spendMerchantName}
        shareToken={shareToken}
        presentation={sheetPresentation}
        checkoutResume={artifactCheckoutResume(currentArtifact.id)}
        securePayment={getMandateSecurePayment(currentArtifact)}
      />
    );
  }

  // Render secure-credential UI for form_input artifacts with isSecureCredential.
  // Two flavors based on credentialKind:
  //   'card'  → AddPaymentCardModal (saves directly via /memories/store-credential
  //             and forwards the new credentialId back to the artifact API).
  //   'login' → SecureCredentialModal (raw form values flow through the artifact
  //             API which then vaults them and scrubs the values).
  if (
    show &&
    currentArtifact?.artifactType === 'form_input' &&
    (currentArtifact as FormInputArtifact).isSecureCredential &&
    !loading &&
    !error
  ) {
    const scArtifact = currentArtifact as FormInputArtifact;
    const credentialKind = (scArtifact as any).credentialKind as
      | 'login'
      | 'card'
      | undefined;

    if (
      hasSecureRequestId(currentArtifact) &&
      scArtifact.isSecureCredential === true
    ) {
      // Live bank challenge: no code field, owner-only live view instead.
      if (isLiveChallengeArtifact(currentArtifact)) {
        return (
          <PaymentChallengeModal
            show
            artifact={currentArtifact}
            secureRequestId={currentArtifact.secureRequestId!}
            threadId={currentArtifact.parentThreadId}
            onClose={onClose}
            onOptimisticAction={onOptimisticAction}
            readOnly={readOnly || submitting}
            shareToken={shareToken}
          />
        );
      }
      return (
        <SecureInputRequestModal
          show
          artifact={currentArtifact}
          secureRequestId={currentArtifact.secureRequestId!}
          onClose={onClose}
          onOptimisticAction={onOptimisticAction}
          readOnly={readOnly || submitting}
          shareToken={shareToken}
          presentation={sheetPresentation}
        />
      );
    }

    if (credentialKind === 'card') {
      const handleCardSuccess = async (card: AddPaymentCardResult) => {
        if (!onSubmitResponse || !currentArtifact) return;
        setSubmitting(true);
        onOptimisticAction?.(currentArtifact, 'submit');
        try {
          // The card was already vaulted client-side; forward the resulting
          // credentialId (and label/network metadata) so the BE can attach it
          // to the artifact for downstream message builders.
          await onSubmitResponse(currentArtifact, {
            credentialStored: true,
            credentialId: card.credentialId,
            cardLastFour: card.cardLastFour,
            cardNetwork: card.cardNetwork,
            label: card.label,
          });
        } finally {
          setSubmitting(false);
        }
      };

      return (
        <AddPaymentCardModal
          show
          onClose={onClose}
          onSuccess={handleCardSuccess}
          existingCredentialId={scArtifact.existingCredentialId}
          onAcknowledge={handleAcknowledge}
          readOnly={readOnly || submitting}
          shareToken={shareToken}
          presentation={sheetPresentation}
        />
      );
    }

    // Default (login) flow. Resolves with the sheet's submit-result so it
    // can render success / failure itself; hosts that close the modal from
    // inside `onSubmitResponse` (in-app sidebars) simply unmount it first.
    const handleSecureSubmit = async (
      values: Record<string, string>,
      opts: SecureCredentialSubmitOptions,
    ): Promise<SecureCredentialSubmitResult> => {
      if (!onSubmitResponse || !currentArtifact) {
        return {
          ok: false,
          error: { message: 'This request can’t be answered from here.' },
        };
      }
      setSubmitting(true);
      onOptimisticAction?.(currentArtifact, 'submit');
      try {
        // Forward the explicit secret-field key set the modal collected.
        // The BE secret-count gate uses this in preference to the key-name
        // heuristic, so user-chosen field names ("magic_word",
        // "memorable_thing") are honoured rather than rejected.
        await onSubmitResponse(currentArtifact, {
          values,
          secretFieldKeys: opts.secretFieldKeys,
        });
        return { ok: true };
      } catch (err) {
        return { ok: false, error: submitErrorFromUnknown(err) };
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <SecureCredentialModal
        artifact={scArtifact}
        onSubmit={handleSecureSubmit}
        onAcknowledge={handleAcknowledge}
        onClose={onClose}
        submitting={submitting}
        readOnly={readOnly}
        presentation={sheetPresentation}
        agentName={
          (
            agents.find(
              (a) => (a.agent_type as string) === 'Personal Assistant',
            ) || agents[0]
          )?.nickname ?? undefined
        }
      />
    );
  }

  // Render full-screen view for form_input artifacts (except confirmation-linked ones like spend approval)
  if (
    show &&
    currentArtifact?.artifactType === 'form_input' &&
    !currentArtifact?.confirmationId &&
    !loading &&
    !error
  ) {
    const fiArtifact = currentArtifact as FormInputArtifact;

    const handleFiChatAbout = onChatAbout
      ? () => onChatAbout(currentArtifact)
      : undefined;

    const handleFiSubmitWithAction = async (
      desiredAction: DesiredActionResponse,
      mandateItems?: Array<{
        actionLine: string;
        descriptionLine: string;
        price: number;
        price_currency: string;
        isDeposit?: boolean;
      }>,
    ) => {
      if (!onSubmitResponse || !currentArtifact) return;
      const mandateOptions = mandateItems?.length
        ? { createMandate: true, mandateItems }
        : undefined;
      const cardSelection = mandateOptions
        ? cardSelectionRef.current
        : undefined;
      setSubmitting(true);
      const deferOptimistic = isOrganisationCard(cardSelection);
      if (!deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
      try {
        await onSubmitResponse(
          currentArtifact,
          { values: formValues, desiredAction },
          mandateOptions,
          cardSelection,
        );
        if (deferOptimistic) onOptimisticAction?.(currentArtifact, 'submit');
      } finally {
        setSubmitting(false);
      }
    };

    const fiPrimaryAgent =
      agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ||
      agents[0];

    return createPortal(
      <>
        <FormInputFullScreen
          artifact={fiArtifact}
          formValues={formValues}
          onFieldChange={handleFormFieldChange}
          onSubmit={handleFormSubmit}
          onSubmitWithAction={handleFiSubmitWithAction}
          onAcknowledge={handleAcknowledge}
          onClose={onClose}
          submitting={submitting}
          readOnly={readOnly}
          pendingSpendItems={pendingSpendItems}
          onApproveSpending={(cardSel) => {
            if (cardSel) cardSelectionRef.current = cardSel;
            return executeFormSubmit(true);
          }}
          onChatAbout={handleFiChatAbout}
          chatButtonText='Chat about this'
          agentName={fiPrimaryAgent?.nickname ?? undefined}
          agentImageUrl={fiPrimaryAgent?.image_url ?? undefined}
          shareToken={shareToken}
          agentWhatsappNumber={agentWhatsappNumber}
        />
      </>,
      document.body,
    );
  }

  // Render full-screen view for yes_or_no artifacts
  if (
    show &&
    currentArtifact?.artifactType === 'yes_or_no' &&
    !loading &&
    !error
  ) {
    const ynArtifact = currentArtifact as YesOrNoArtifact;

    const handleYnChatAbout = onChatAbout
      ? () => onChatAbout(currentArtifact)
      : undefined;

    const handleYnReject = async () => {
      if (!onSubmitResponse || !currentArtifact) return;
      setSubmitting(true);
      onOptimisticAction?.(currentArtifact, 'dismiss');
      try {
        await onSubmitResponse(currentArtifact, {
          approved: false,
          selection: 'no',
        });
      } finally {
        setSubmitting(false);
      }
    };

    const handleYnApproveConfirmation = async () => {
      if (!onSubmitResponse || !currentArtifact) return;
      setSubmitting(true);
      onOptimisticAction?.(currentArtifact, 'submit');
      try {
        await onSubmitResponse(currentArtifact, {
          approved: true,
          selection: 'yes',
        });
      } finally {
        setSubmitting(false);
      }
    };

    const handleYnSubmitWithAction = async (
      desiredAction: DesiredActionResponse,
      mandateItems?: Array<{
        actionLine: string;
        descriptionLine: string;
        price: number;
        price_currency: string;
        isDeposit?: boolean;
      }>,
      cardSel?: CardSelection,
    ) => {
      if (cardSel) cardSelectionRef.current = cardSel;
      await executeSubmit(!!mandateItems, desiredAction, mandateItems);
    };

    const ynPrimaryAgent =
      agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ||
      agents[0];

    return createPortal(
      <>
        <YesOrNoFullScreen
          artifact={ynArtifact}
          selectedChoices={selectedChoices}
          onChoiceSelect={handleChoiceSelect}
          onSubmit={handleSubmit}
          onSubmitWithAction={handleYnSubmitWithAction}
          onAcknowledge={handleAcknowledge}
          onClose={onClose}
          submitting={submitting}
          readOnly={readOnly}
          colorVariants={colorVariants}
          pendingSpendItems={pendingSpendItems}
          isConfirmationLinked={isConfirmationLinked}
          confirmationMethodName={confirmationMethodName}
          confirmationMethodInput={confirmationMethodInput}
          onReject={isConfirmationLinked ? handleYnReject : undefined}
          onApproveConfirmation={
            isConfirmationLinked ? handleYnApproveConfirmation : undefined
          }
          onChatAbout={handleYnChatAbout}
          chatButtonText='Chat about this'
          agentName={ynPrimaryAgent?.nickname ?? undefined}
          agentImageUrl={ynPrimaryAgent?.image_url ?? undefined}
          connectedEmails={connectedEmails}
          shareToken={shareToken}
          agentWhatsappNumber={agentWhatsappNumber}
        />
      </>,
      document.body,
    );
  }

  // Render full-screen view for multiple_choice artifacts
  if (
    show &&
    currentArtifact?.artifactType === 'multiple_choice' &&
    !loading &&
    !error
  ) {
    const mcArtifact = currentArtifact as MultipleChoiceArtifact;

    const handleMcChatAbout = onChatAbout
      ? () => onChatAbout(currentArtifact)
      : undefined;

    const handleMcReject = async () => {
      if (!onSubmitResponse || !currentArtifact) return;
      setSubmitting(true);
      onOptimisticAction?.(currentArtifact, 'dismiss');
      try {
        await onSubmitResponse(currentArtifact, {
          approved: false,
          selection: 'no',
        });
      } finally {
        setSubmitting(false);
      }
    };

    const handleMcApproveConfirmation = async () => {
      if (!onSubmitResponse || !currentArtifact) return;
      setSubmitting(true);
      onOptimisticAction?.(currentArtifact, 'submit');
      try {
        await onSubmitResponse(currentArtifact, {
          approved: true,
          selection: 'yes',
        });
      } finally {
        setSubmitting(false);
      }
    };

    const handleSubmitWithAction = async (
      desiredAction: DesiredActionResponse,
      mandateItems?: Array<{
        actionLine: string;
        descriptionLine: string;
        price: number;
        price_currency: string;
        isDeposit?: boolean;
      }>,
      cardSel?: CardSelection,
      travelSelection?: TravelBookingSelection,
    ) => {
      // Spending approval is handled inside MultipleChoiceFullScreen.
      // If mandateItems are provided, the user has already approved spending.
      if (cardSel) cardSelectionRef.current = cardSel;
      await executeSubmit(
        !!mandateItems,
        desiredAction,
        mandateItems,
        travelSelection,
      );
    };

    const primaryAgent =
      agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ||
      agents[0];

    const handleCheckSpendingIntent = async (
      message: string,
      context?: string,
    ) => {
      const token = await getAccessTokenSilently();
      const selectedItems = selectedChoices
        .map((id) => mcArtifact.choices.find((c) => c.choiceId === id))
        .filter(Boolean)
        .map((c) => ({
          actionLine: c!.choiceTitle,
          descriptionLine: c!.choiceBlurb || '',
        }));
      const res = await api.post<{
        spendingIntent: boolean;
        spendType?: 'buy' | 'reserve';
        spendDescription?: string;
        estimatedAmount?: number;
        currency?: string;
      }>(
        '/agents/check-spending-intent',
        {
          message,
          items: selectedItems.length > 0 ? selectedItems : undefined,
          context,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    };

    return createPortal(
      <>
        <MultipleChoiceFullScreen
          artifact={mcArtifact}
          selectedChoices={selectedChoices}
          onChoiceSelect={handleChoiceSelect}
          onSubmitWithAction={handleSubmitWithAction}
          onSubmit={handleSubmit}
          onAcknowledge={handleAcknowledge}
          onClose={onClose}
          submitting={submitting}
          readOnly={readOnly}
          colorVariants={colorVariants}
          pendingSpendItems={pendingSpendItems}
          isConfirmationLinked={isConfirmationLinked}
          onReject={isConfirmationLinked ? handleMcReject : undefined}
          onApproveConfirmation={
            isConfirmationLinked ? handleMcApproveConfirmation : undefined
          }
          onChatAbout={handleMcChatAbout}
          chatButtonText={getChatButtonText()}
          agentName={primaryAgent?.nickname ?? agentNameProp}
          connectedEmails={connectedEmails}
          shareToken={shareToken}
          onCheckSpendingIntent={handleCheckSpendingIntent}
          agentWhatsappNumber={agentWhatsappNumber}
        />
      </>,
      document.body,
    );
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        dialogClassName='response-artifact-modal'
      >
        <Modal.Header>{renderNavigationHeader()}</Modal.Header>

        <Modal.Body>
          {loading ? (
            renderLoadingSkeleton()
          ) : error ? (
            renderError()
          ) : (
            <>
              <h2 className='artifact-title'>{getTitle()}</h2>
              {getOverview() && (
                <div className='artifact-overview'>
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {getOverview()!}
                  </ReactMarkdown>
                </div>
              )}
              {renderContent()}
            </>
          )}
        </Modal.Body>

        {!readOnly && !loading && !error && (
          <Modal.Footer>
            <div className='chat-about-this'>
              {(() => {
                // Build reply-to data from current selections
                const getReplyItems = (): ReplyToItem[] => {
                  if (currentArtifact.artifactType === 'multiple_choice') {
                    const artifact = currentArtifact as MultipleChoiceArtifact;
                    const selected = selectedChoices
                      .map((choiceId) =>
                        artifact.choices.find((c) => c.choiceId === choiceId),
                      )
                      .filter(Boolean) as ArtifactChoice[];
                    if (selected.length > 0) {
                      return selected.map((c) => ({
                        id: `artifact:${currentArtifact.id}:${c.choiceId}`,
                        actionLine: artifact.actionVerb || 'Reply to',
                        descriptionLine: c.choiceTitle,
                        extraContext: `Response Artifact ID: ${currentArtifact.id}`,
                        price: c.definitivePrice ?? (c as any).definitive_price,
                        price_currency:
                          c.priceCurrency ?? (c as any).price_currency ?? 'GBP',
                      }));
                    }
                  }
                  return [
                    {
                      id: `artifact:${currentArtifact.id}`,
                      actionLine: 'Reply to',
                      descriptionLine: currentArtifact.title || 'Task',
                      extraContext: `Response Artifact ID: ${currentArtifact.id}`,
                      bookable: true,
                    },
                  ];
                };

                if (onChatAbout) {
                  // Open mini thread panel for artifact discussion
                  return (
                    <button
                      className='chat-btn'
                      onClick={() => {
                        onChatAbout(currentArtifact);
                      }}
                    >
                      <SpeechBubbleIcon />
                      {getChatButtonText()}
                    </button>
                  );
                }

                if (navigateOnChat) {
                  // Build URL with reply params for navigation
                  const replyItems = getReplyItems();
                  const params = new URLSearchParams();
                  // Include agent_id so ChatSidebar can resolve the agent image
                  const primaryAgent =
                    agents.find(
                      (a) => (a.agent_type as string) === 'Personal Assistant',
                    ) || agents[0];
                  if (primaryAgent?.id) {
                    params.set('agent_id', primaryAgent.id);
                  }
                  params.set('thread_id', currentArtifact.parentThreadId);
                  if (replyItems.length === 1) {
                    const item = replyItems[0];
                    params.set('reply_action', item.actionLine);
                    params.set('reply_desc', item.descriptionLine);
                    if (item.extraContext) {
                      params.set('reply_context', item.extraContext);
                    }
                    if (item.bookable) params.set('reply_bookable', '1');
                    if (item.price != null)
                      params.set('reply_price', String(item.price));
                    if (item.price_currency)
                      params.set('reply_price_currency', item.price_currency);
                  } else {
                    replyItems.forEach((item, i) => {
                      params.set(`reply_action_${i}`, item.actionLine);
                      params.set(`reply_desc_${i}`, item.descriptionLine);
                      if (item.extraContext)
                        params.set(`reply_context_${i}`, item.extraContext);
                      if (item.bookable) params.set(`reply_bookable_${i}`, '1');
                      if (item.price != null)
                        params.set(`reply_price_${i}`, String(item.price));
                      if (item.price_currency)
                        params.set(
                          `reply_price_currency_${i}`,
                          item.price_currency,
                        );
                    });
                  }
                  const chatUrl = `/conversations?${params.toString()}`;

                  return (
                    <a
                      href={chatUrl}
                      className='chat-btn'
                      onClick={(e) => {
                        // For regular click, use SPA navigation
                        if (!e.metaKey && !e.ctrlKey) {
                          e.preventDefault();
                          onClose();
                          navigate(chatUrl);
                        }
                        // For Cmd/Ctrl+Click, browser opens <a> in new tab naturally
                      }}
                    >
                      <SpeechBubbleIcon />
                      {getChatButtonText()}
                    </a>
                  );
                }

                // Default: in-context behavior (already viewing the thread)
                return (
                  <button
                    className='chat-btn'
                    onClick={() => {
                      const replyItems = getReplyItems();
                      replyItems.forEach((item) => addReplyTo(item));
                      onClose();
                    }}
                  >
                    <SpeechBubbleIcon />
                    {getChatButtonText()}
                  </button>
                );
              })()}
            </div>
            <div className='action-buttons'>
              {isConfirmationLinked ? (
                <>
                  <button
                    className='reject-btn'
                    onClick={async () => {
                      if (!onSubmitResponse || !currentArtifact) return;
                      setSubmitting(true);
                      onOptimisticAction?.(currentArtifact, 'dismiss');
                      try {
                        await onSubmitResponse(currentArtifact, {
                          approved: false,
                          selection: 'no',
                        });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    disabled={submitting}
                  >
                    Reject
                  </button>
                  <button
                    className='action-btn'
                    onClick={async () => {
                      if (!onSubmitResponse || !currentArtifact) return;
                      setSubmitting(true);
                      onOptimisticAction?.(currentArtifact, 'submit');
                      try {
                        if (currentArtifact.artifactType === 'form_input') {
                          await onSubmitResponse(currentArtifact, {
                            approved: true,
                            values: formValues,
                          });
                        } else {
                          await onSubmitResponse(currentArtifact, {
                            approved: true,
                            selection: 'yes',
                          });
                        }
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    disabled={submitting}
                  >
                    {currentArtifact?.submitLabel || 'Approve'}
                  </button>
                </>
              ) : currentArtifact.artifactType === 'form_input' ? (
                <>
                  <button
                    className='no-longer-needed-btn'
                    onClick={handleAcknowledge}
                    disabled={submitting}
                  >
                    <NoLongerNeededIcon /> Remove task
                  </button>
                  <button
                    className='action-btn'
                    onClick={handleFormSubmit}
                    disabled={submitting || !isFormValid()}
                  >
                    {getActionButtonText()}
                  </button>
                </>
              ) : getActionButtonText() && selectedChoices.length > 0 ? (
                <button
                  className='action-btn'
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {getActionButtonText()}
                </button>
              ) : (
                <button
                  className='no-longer-needed-btn'
                  onClick={handleAcknowledge}
                  disabled={submitting}
                >
                  <NoLongerNeededIcon /> Remove task
                </button>
              )}
            </div>
          </Modal.Footer>
        )}

        {/* Spending approval bottom-sheet within this modal */}
        <SpendingApprovalModal
          inline
          show={showSpendingApproval}
          onApprove={handleApproveSpending}
          onCancel={handleCancelSpending}
          assistantName={
            (
              agents.find(
                (a) => (a.agent_type as string) === 'Personal Assistant',
              ) || agents[0]
            )?.nickname || 'Secretary'
          }
          isDeposit={isDepositApproval}
          items={pendingSpendItems.map((item) => ({
            id: item.id,
            actionLine: item.actionLine,
            descriptionLine: item.descriptionLine,
            bookable: true,
            price: item.price,
            price_currency: item.price_currency,
            purchaseSummary: item.purchaseSummary,
            purchaseLink: item.purchaseLink,
            delivery: item.delivery,
            deliveryAddress: item.deliveryAddress,
          }))}
          shareToken={shareToken}
        />
      </Modal>
    </>
  );
};

export default ResponseArtifactModal;
